/**
 * Validações globais para formulários
 * Pode ser importado em qualquer componente Vue
 */

/**
 * Valida CPF brasileiro
 * @param {string} value - CPF com ou sem formatação
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validateCPF(value) {
  if (!value) return 'Campo obrigatório'

  const cpf = value.replace(/[^\d]/g, '')
  if (cpf.length !== 11) return 'CPF deve ter 11 dígitos'

  // Validação do primeiro dígito verificador
  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cpf.charAt(9))) return 'CPF inválido'

  // Validação do segundo dígito verificador
  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cpf.charAt(10))) return 'CPF inválido'

  return true
}

/**
 * Verifica se CPF é válido (retorna apenas boolean)
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {boolean}
 */
export function isValidCPF(cpf) {
  return validateCPF(cpf) === true
}

/**
 * Valida CNPJ brasileiro
 * @param {string} value - CNPJ com ou sem formatação
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validateCNPJ(value) {
  if (!value) return 'Campo obrigatório'

  const cnpj = value.replace(/[^\d]/g, '')
  if (cnpj.length !== 14) return 'CNPJ deve ter 14 dígitos'

  // Validação do primeiro dígito verificador
  let sum = 0
  let weight = 5
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder
  if (digit1 !== parseInt(cnpj.charAt(12))) return 'CNPJ inválido'

  // Validação do segundo dígito verificador
  sum = 0
  weight = 6
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder
  if (digit2 !== parseInt(cnpj.charAt(13))) return 'CNPJ inválido'

  return true
}

/**
 * Verifica se CNPJ é válido (retorna apenas boolean)
 * @param {string} cnpj - CNPJ com ou sem formatação
 * @returns {boolean}
 */
export function isValidCNPJ(cnpj) {
  return validateCNPJ(cnpj) === true
}

/**
 * Valida e-mail
 * @param {string} value - E-mail
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validateEmail(value) {
  if (!value) return 'Campo obrigatório'
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return pattern.test(value) || 'E-mail inválido'
}

/**
 * Valida telefone brasileiro (celular ou fixo)
 * @param {string} value - Telefone com ou sem formatação
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validatePhone(value) {
  if (!value) return 'Campo obrigatório'
  const phone = value.replace(/[^\d]/g, '')

  // Celular: 11 dígitos (DDD + 9 + 8 dígitos)
  // Fixo: 10 dígitos (DDD + 8 dígitos)
  if (phone.length !== 10 && phone.length !== 11) {
    return 'Telefone deve ter 10 ou 11 dígitos'
  }

  return true
}

/**
 * Valida CEP brasileiro
 * @param {string} value - CEP com ou sem formatação
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validateCEP(value) {
  if (!value) return 'Campo obrigatório'
  const cep = value.replace(/[^\d]/g, '')
  if (cep.length !== 8) return 'CEP deve ter 8 dígitos'

  return true
}

/**
 * Valida campo obrigatório
 * @param {*} value - Valor do campo
 * @returns {boolean|string} - true se válido, mensagem de erro se inválido
 */
export function validateRequired(value) {
  return !!value || 'Campo obrigatório'
}

/**
 * Valida idade de criança para o motor de reservas
 * @param {number|string} value - Idade informada
 * @returns {boolean|string}
 */
export function validateChildAge(value) {
  if (value === null || value === undefined || value === '') return 'Campo obrigatório'
  const age = Number(value)
  if (!Number.isInteger(age) || age < 0) return 'Informe uma idade válida'
  if (age > 17) return 'Idade deve ser entre 0 e 17 anos'

  return true
}

/**
 * Objeto com todas as validações para uso em componentes Vue
 * Exemplo de uso:
 *
 * import { validationRules } from '@/utils/validators'
 *
 * export default {
 *   data() {
 *     return {
 *       rules: validationRules
 *     }
 *   }
 * }
 */
export const validationRules = {
  required: validateRequired,
  cpf: validateCPF,
  cnpj: validateCNPJ,
  email: validateEmail,
  phone: validatePhone,
  cep: validateCEP,
  childAge: validateChildAge,
}

export default validationRules
